import React from 'react'
import PropTypes from 'prop-types'
import {Button, Flex, Text, useStyleConfig, Img} from '@chakra-ui/react'
import {Link} from 'react-router-dom'
import useDamImage from '../../../custom-hooks/use-dam-image'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const PlpGridCard = ({
    title,
    badge,
    subtitle,
    redirects,
    footer,
    images,
    textColor = '',
    colSpan = 1,
    categoriesData,
}) => {
    const styles = useStyleConfig('PlpGridCard')
    const {locale} = useMultiSite()
    const damImage = useDamImage(images)
    const isColorWhite = textColor?.toLowerCase() === 'white' || false
    const titleColorRes = isColorWhite ? 'foreground.neutral.inverse' : '#001787'
    const iconColorRes = isColorWhite ? 'foreground.neutral.inverse' : 'foreground.neutral.default'
    const subtitleColorRes = isColorWhite ? 'foreground.neutral.inverse' : '#374B8A'

    const {data: productsData} = useProducts(
        {
            parameters: {
                ids:
                    redirects?.productsfcc?.data?.length > 0
                        ? redirects.productsfcc.data[0].id
                        : '',
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'plp-grid-index',
            },
        },
        {
            enabled: redirects?.productsfcc?.data?.length > 0,
        },
    )
    const {url, label, isAbsolute} = getGlobalLink({
        link: redirects,
        categoriesData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })
    const hasValidCta = Boolean(url && label)

    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: url,
          }
    return (
        <Flex
            {...styles.container}
            gridColumn={`span ${colSpan}`}
            background={
                damImage?.url
                    ? `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 21.28%, rgba(0, 0, 0, 0.70) 90.43%), url(${damImage.url}) lightgray 50% / cover no-repeat`
                    : 'none'
            }
            borderRadius="large-1"
        >
            {(badge || title || subtitle) && (
                <Flex {...styles.headerContainer}>
                    {badge && (
                        <Flex {...styles.badgeContainer}>
                            <Text {...styles.badge} color={titleColorRes} tabIndex={0}>
                                {badge}
                            </Text>
                        </Flex>
                    )}
                    {(title || subtitle) && (
                        <Flex {...styles.textContainer}>
                            {title && (
                                <Text {...styles.title} color={titleColorRes} tabIndex={0} as="h2">
                                    {title}
                                </Text>
                            )}
                            {subtitle && (
                                <Text {...styles.subtitle} color={titleColorRes} tabIndex={0}>
                                    {subtitle}
                                </Text>
                            )}
                        </Flex>
                    )}
                </Flex>
            )}
            {hasValidCta ? (
                <Button
                    {...buttonProps}
                    {...styles.button}
                    variant="outline-not-rounded"
                    size="large"
                    aria-label={label}
                >
                    {label ? label : ''}
                </Button>
            ) : (
                <Flex w="100%" {...styles.footerContainer}>
                    <Flex width="100%" {...styles.footerInner}>
                        {footer?.length > 0 &&
                            footer?.map((item) => {
                                return (
                                    <Flex
                                        key={`footer-item-${item?._metadata?.uid}`}
                                        {...styles.footerItem}
                                    >
                                        {item?.icon?.url && (
                                            <Img
                                                {...styles.footerIcon}
                                                src={item.icon.url}
                                                alt=""
                                                color={iconColorRes}
                                            />
                                        )}
                                        <Flex {...styles.footerTextGroup}>
                                            <Text
                                                as="h2"
                                                {...styles.footerTitle}
                                                color={titleColorRes}
                                                tabIndex={0}
                                            >
                                                {item?.title}
                                            </Text>
                                            <Text
                                                {...styles.footerSubtitle}
                                                color={subtitleColorRes}
                                                tabIndex={0}
                                            >
                                                {item?.subtitle}
                                            </Text>
                                        </Flex>
                                    </Flex>
                                )
                            })}
                    </Flex>
                </Flex>
            )}
        </Flex>
    )
}

PlpGridCard.propTypes = {
    title: PropTypes.string,
    images: PropTypes.object,
    redirects: PropTypes.object,
    categoriesData: PropTypes.object,
    colSpan: PropTypes.number,
    badge: PropTypes.string,
    subtitle: PropTypes.string,
    footer: PropTypes.array,
    textColor: PropTypes.string,
}

export default PlpGridCard
